import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@Utils';

import Badge from '@Components/Badge/Badge';
import { ChainAvatar } from '@Components/ConnectedChainCard';
import { Button } from '@Components/ui-v2/button';
import { FieldServiceStore } from '@Models';
import CoverageStoresModal from '../components/CoverageStoresModal';
import { getRetailerColor, getRetailerName, getRetailerShort, UPPERCASE_LABEL_CLASS_NAME } from '../constants';

interface CoverageSectionProps {
  /** The contract's retailer. One contract covers one retailer. */
  retailer: string;
  /** Covered stores only, straight from the field service detail response. */
  stores: FieldServiceStore[];
  /** Active stores for this retailer, covered or not. */
  totalStores: number;
}

const CoverageSection = ({ retailer, stores, totalStores }: CoverageSectionProps) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const coveredCodes = useMemo(() => new Set(stores.map((store) => store.store_id)), [stores]);

  const isAvailable = coveredCodes.size > 0;

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-6">
      <div>
        <span className={cn('block', UPPERCASE_LABEL_CLASS_NAME)}>{t('FieldService.Sections.Coverage')}</span>
        <p className="mb-0 mt-2 text-xs text-gray-500">{t('FieldService.Coverage.Description')}</p>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 px-4 py-3">
        <ChainAvatar
          size="sm"
          initials={getRetailerShort(retailer)}
          color={getRetailerColor(retailer)}
          alt={getRetailerName(retailer)}
        />
        <div className="min-w-0 flex-1 basis-40">
          <div className="truncate text-[13px] font-bold text-gray-900">{getRetailerName(retailer)}</div>
          <div className="text-xs text-gray-500">
            {t('FieldService.Coverage.TotalStores', { count: totalStores })}
            {' · '}
            {t('FieldService.Coverage.StoresCovered', { count: coveredCodes.size })}
          </div>
        </div>
        <div className="ml-auto flex flex-shrink-0 items-center gap-3">
          <Badge
            variant={isAvailable ? 'green' : 'gray'}
            label={isAvailable ? t('FieldService.Coverage.Available') : t('FieldService.Coverage.Unavailable')}
            showDot
            size="sm"
            className={cn('rounded-full', !isAvailable && 'text-gray-500')}
          />
          {/* Shown even with zero coverage, since seeing what is uncovered is the point. */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsModalOpen(true)}
            className="h-8 flex-shrink-0 border-gray-300 bg-white text-xs font-semibold text-gray-900 hover:bg-gray-50">
            {t('FieldService.Coverage.ViewStores')}
          </Button>
        </div>
      </div>

      {isModalOpen && (
        <CoverageStoresModal
          retailerId={retailer}
          coveredCodes={coveredCodes}
          totalStores={totalStores}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </div>
  );
};

export default CoverageSection;
